import { memo } from 'react';

/**
 * Pure presenter для OnlineIndicator — UI badge без state logic.
 *
 * States (ADR-0019 §Q11.5):
 *   - allSynced (online + pendingCount === 0): **hidden** (v0.4.1 H2.3 design fix).
 *     Раньше рендерилась зелёная точка «Все изменения сохранены» — Aibek preview
 *     verify зафиксировал что happy state не нуждается в визуальном сигнале (success
 *     является default expectation, noise reduction). Component возвращает `null`.
 *   - offline + pendingCount === 0: янтарная точка, статус «Нет соединения»
 *   - pendingCount > 0 (online OR offline): янтарная точка + count badge, статус «N изменений ожидают синхронизации»
 *
 * A11y (P-022):
 *   - role='status' + aria-live='polite' на root <div> (НЕ на button — конфликт ролей)
 *   - aria-label dynamic Russian copy с правильной плюрализацией
 *   - В MVP — non-interactive div (Phase 9+ → button с modal)
 *
 * Plurals (Russian, 3 forms):
 *   - 1, 21, 31… → «изменение»
 *   - 2-4, 22-24… → «изменения»
 *   - 5-20, 25-30… → «изменений»
 *
 * References: ADR-0005 (Container/Presenter), ADR-0019 §Q11.5, P-022
 */
export interface OnlineIndicatorViewProps {
  online: boolean;
  pendingCount: number;
}

function pluralRu(n: number, forms: readonly [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

const FORMS_IZMENENIE = ['изменение', 'изменения', 'изменений'] as const;

export const OnlineIndicatorView = memo(function OnlineIndicatorView({
  online,
  pendingCount,
}: OnlineIndicatorViewProps) {
  const allSynced = online && pendingCount === 0;
  const offlineNoPending = !online && pendingCount === 0;

  // v0.4.1 (H2.3): hide indicator on happy path — no visual noise when offline-first
  // sync is keeping up. Только attention-states (offline / pending) рендерятся.
  if (allSynced) return null;

  const ariaLabel = offlineNoPending
    ? 'Нет соединения'
    : `${pendingCount} ${pluralRu(pendingCount, FORMS_IZMENENIE)} ожидают синхронизации`;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      data-testid="online-indicator"
      className="inline-flex items-center justify-center gap-1.5 rounded-full border px-2 text-xs min-h-tap min-w-tap border-warning/40 bg-warning/10 text-warning"
    >
      <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
      {pendingCount > 0 && (
        <span aria-hidden="true" className="font-medium">
          {pendingCount}
        </span>
      )}
    </div>
  );
});
